/**
 * A common regular expression for validating a date in the format
 * `MM/YYYY`, where `MM` is a two-digit month (01 to 12) and `YYYY` is a four-digit year.
 * This format is often used for credit card expiration dates.
 *
 * @example ```ts
 * const isValid = mmYYYYRegExp.test('12/2023'); // true
 * const isInvalid = mmYYYYRegExp.test('13/2023'); // false
 */
export const mmYYYYRegExp = /^(0[1-9]|1[0-2])\/\d{4}$/;

/**
 * A regular expression that matches a locale code in the format
 * `xx-XX`, where `xx` is a two-letter language code and `XX` is a two-letter country code.
 * This format is commonly used in internationalization to specify language
 * and region.
 *
 * This version allows the country code to be optional, so it matches both `xx` and `xx-XX` formats.
 *
 * @example
 * langLocaleRegExp.test('en-US'); // true
 * langLocaleRegExp.test('en'); // true
 * langLocaleRegExp.test('en-US-GB'); // false
 */
export const langLocaleRegex = /^[a-zA-Z]{2}(-[a-zA-Z]{2})?$/;

/**
 * A regular expression that matches a locale code in the format
 * `xx-XX`, where `xx` is a two-letter language code and `XX` is a two-letter country code.
 * This format is commonly used in internationalization to specify language
 * and region.
 *
 * @example
 * langLocaleRegExp.test('en-US'); // true
 * langLocaleRegExp.test('en'); // false
 * langLocaleRegExp.test('en-US-GB'); // false
 */
export const langLocaleStrictRegex = /^[a-zA-Z]{2}-[a-zA-Z]{2}$/;

/**
 * Escapes any characters in the provided string that would have special meaning
 * in a regular expression. This is useful when creating a regular expression
 * programmatically.
 * @param str - A string which may contain characters with special meaning in a
 * regular expression.
 * @returns The provided string with all characters that have special meaning
 * in a regular expression escaped.
 * @example ```ts
 * const regex = new RegExp(escapeRegEx('abc.def')); // /abc\.def/
 * ```
 */
export const escapeRegEx = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

/**
 * Joins an array of regex patterns into a single regex pattern that matches
 * any of the provided patterns. If the array is empty, it returns a pattern
 * that matches nothing.
 * @param patterns - An array of string regex patterns to join.
 * @returns A string representing a regex pattern that matches any of the provided patterns.
 * @example
 * unionRegexPatterns([]); // '(?!)'
 * unionRegexPatterns(['abc', 'def']); // '(?:abc)|(?:def)'
 */
export const unionRegexPatterns = (patterns: ReadonlyArray<string>): string => {
  if (patterns.length === 0) return '(?!)'; // Matches nothing if no patterns are provided.
  // Not to be confused with `(?:)`, which matches the empty string and therefore everything.

  return patterns.map((pattern) => `(?:${pattern})`).join('|');
};

/**
 * This regular expression matches a string that starts with a number
 * (integer or decimal) followed optionally by any letter(s).
 * @example ```ts
 * numberAndCharactersRegex.test('12'); // true
 * numberAndCharactersRegex.test('123.3'); // true
 * numberAndCharactersRegex.test('1T'); //true
 * ```
 */
export const numberAndCharactersRegex = /^(\d+(?:\.\d+)?)([A-Za-z]*)$/;
